import React, { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface Props {
  onSend: (content: string) => Promise<void> | void;
  onFileSelected: (file: File) => void;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  compact?: boolean;
  /** Hide the attach control where the surface has no upload path (assistant). */
  hideAttachment?: boolean;
}

const DEFAULT_MAX = 4000;
// A phone's on-screen keyboard has no Shift+Enter, so there Enter must be a line break and
// the send button sends. Only narrow screens: a tablet may have a hardware keyboard, where
// Enter sending is expected (the cost: a tablet with an on-screen keyboard cannot add a line break).
export const PHONE_KEYBOARD_QUERY = "(pointer: coarse) and (max-width: 767.98px)";

const ChatComposer: React.FC<Props> = ({
  onSend,
  onFileSelected,
  disabled,
  maxLength = DEFAULT_MAX,
  placeholder,
  compact = false,
  hideAttachment = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    // textarea.style.lineBreak = "anywhere";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  const handleSend = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (trimmed.length > maxLength) {
      toast.error(t("message_too_long"));
      return;
    }
    setBusy(true);
    try {
      await onSend(trimmed);
      setValue("");
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch {
      // toast handled by caller
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    if (window.matchMedia?.(PHONE_KEYBOARD_QUERY).matches) return;
    e.preventDefault();
    handleSend();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelected(file);
    }
    e.target.value = "";
  };

  const canSend = value.trim().length > 0 && !disabled && !busy;

  return (
    <div className={`border-t border-gray-800 ${compact ? "px-2 py-2 bg-gray-900" : "px-3 py-2.5 bg-gray-950"} flex items-end gap-2`}>
      {!hideAttachment && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
      )}
      
      {/* Sleek unified pill container for attachment and text input */}
      <div className="flex-1 flex items-end bg-gray-900 border border-gray-800 hover:border-gray-700 focus-within:border-blue-500/80 focus-within:ring-2 focus-within:ring-blue-500/15 rounded-xl transition-all duration-200 px-2 py-1 gap-1.5">
        
        {!hideAttachment && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || busy}
          className="p-1.5 max-lg:p-3 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-all duration-150 shrink-0 mb-0.5"
          aria-label={t("attach_file")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            className="max-lg:h-5 max-lg:w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.51a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          disabled={disabled || busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t("type_a_message")}
          rows={1}
          style={{lineBreak:"anywhere"}}
          className="flex-1 min-w-0 resize-none bg-transparent text-gray-100 text-base lg:text-sm py-1.5 px-1 focus:outline-none placeholder-gray-500 leading-normal lg:leading-normal max-h-32 overflow-y-auto min-h-[32px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className={`p-1.5 max-lg:p-3 rounded-lg transition-all duration-200 shrink-0 mb-0.5 flex items-center justify-center ${
            canSend
              ? "bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-600/10 active:scale-95"
              : "bg-gray-800 text-gray-600 cursor-not-allowed"
          }`}
          aria-label={t("send")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            className="max-lg:h-5 max-lg:w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatComposer;
